import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Video, Camera, Mic, Sun, Clock, FileVideo, CheckCircle, 
  XCircle, AlertTriangle, Monitor, Volume2, Users, Shirt, MapPin,
  Film, Award, Sparkles, HelpCircle, ChevronDown, ChevronUp
} from 'lucide-react';

export default function VideoGuidelinesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('brandstage');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleBack} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#C41E3A]/20 flex items-center justify-center">
              <Video className="w-7 h-7 text-[#C41E3A]" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white">Video Quality Guidelines</h1>
              <p className="text-zinc-400">Standards for professional, impactful video content</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 p-1 bg-zinc-900 rounded-xl">
          <button
            onClick={() => setActiveTab('brandstage')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'brandstage' 
                ? 'bg-[#C41E3A] text-white' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Award className="w-4 h-4" />
            BrandStage Productions
          </button>
          <button
            onClick={() => setActiveTab('self-upload')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'self-upload' 
                ? 'bg-[#C41E3A] text-white' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <FileVideo className="w-4 h-4" />
            Self-Upload Standards
          </button>
        </div>

        {/* BrandStage Productions Tab */}
        {activeTab === 'brandstage' && (
          <div className="space-y-8">
            {/* Introduction */}
            <div className="bg-gradient-to-br from-[#C41E3A]/20 to-transparent border border-[#C41E3A]/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C41E3A]" />
                The BrandStage Promise
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                When BrandStage produces your video content—whether it's a leadership interview, corporate film, 
                product showcase, or testimonial—we adhere to rigorous production standards. Our goal is to create 
                content that elevates your brand and builds trust with your audience.
              </p>
            </div>

            {/* What We Deliver */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">What We Deliver</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: <Camera className="w-5 h-5" />, title: "Professional Cinematography", desc: "Multi-camera setups, professional framing, and cinematic composition" },
                  { icon: <Mic className="w-5 h-5" />, title: "Broadcast-Quality Audio", desc: "Lapel microphones, audio monitoring, and noise-free recording" },
                  { icon: <Sun className="w-5 h-5" />, title: "Professional Lighting", desc: "Three-point lighting setup ensuring flattering, consistent illumination" },
                  { icon: <Film className="w-5 h-5" />, title: "Post-Production Polish", desc: "Color grading, audio mastering, graphics, and branded elements" }
                ].map((item, i) => (
                  <div key={i} className="bg-zinc-800/50 rounded-xl p-4 flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#C41E3A]/20 flex items-center justify-center text-[#C41E3A] flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{item.title}</h4>
                      <p className="text-zinc-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Video Types & Standards */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Production Standards by Video Type</h3>
              
              {/* Leadership Interviews */}
              <div className="mb-6 pb-6 border-b border-zinc-800">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  Leadership Interviews
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500 mb-1">Technical Specs</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Resolution: 4K capture, 1080p delivery</li>
                      <li>• Frame rate: 25fps (PAL) or 30fps (NTSC)</li>
                      <li>• Audio: 48kHz, stereo or dual-mono</li>
                      <li>• Duration: 2-5 minutes optimal</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-zinc-500 mb-1">Content Guidelines</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Pre-interview briefing provided</li>
                      <li>• Teleprompter available if needed</li>
                      <li>• Multiple takes until comfortable</li>
                      <li>• B-roll of workspace/team optional</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Corporate Films */}
              <div className="mb-6 pb-6 border-b border-zinc-800">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Film className="w-4 h-4 text-purple-400" />
                  Corporate Films
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500 mb-1">Technical Specs</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Resolution: 4K capture and delivery</li>
                      <li>• Cinematic color grading</li>
                      <li>• Professional voiceover (optional)</li>
                      <li>• Duration: 3-10 minutes</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-zinc-500 mb-1">Production Includes</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Script development assistance</li>
                      <li>• Location scouting</li>
                      <li>• Motion graphics & titles</li>
                      <li>• Licensed background music</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Product Films */}
              <div className="mb-6 pb-6 border-b border-zinc-800">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-green-400" />
                  Product Showcases
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500 mb-1">Technical Specs</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Macro and detail shots</li>
                      <li>• 360° product views where applicable</li>
                      <li>• Demo/functionality capture</li>
                      <li>• Duration: 1-3 minutes</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-zinc-500 mb-1">Production Includes</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Product styling</li>
                      <li>• Explainer graphics/annotations</li>
                      <li>• Feature callouts</li>
                      <li>• Comparison shots (optional)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Testimonials */}
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-400" />
                  Video Testimonials
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500 mb-1">Technical Specs</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Resolution: 1080p minimum</li>
                      <li>• Natural, authentic delivery</li>
                      <li>• Clear audio capture</li>
                      <li>• Duration: 30 sec - 2 minutes</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-zinc-500 mb-1">We Ensure</p>
                    <ul className="text-zinc-300 space-y-1">
                      <li>• Comfortable interview environment</li>
                      <li>• Guiding questions (not scripted)</li>
                      <li>• Client/customer identification</li>
                      <li>• Consent documentation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Produced by BrandStage Badge */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"Produced by BrandStage" Badge</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed">
                    All videos produced by BrandStage receive our <span className="text-green-400 font-semibold">"Produced by BrandStage"</span> badge, 
                    signaling to viewers that the content meets professional broadcast standards. This badge indicates 
                    production quality and is distinct from profile verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Self-Upload Standards Tab */}
        {activeTab === 'self-upload' && (
          <div className="space-y-8">
            {/* Introduction */}
            <div className="bg-gradient-to-br from-blue-500/20 to-transparent border border-blue-500/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <FileVideo className="w-5 h-5 text-blue-400" />
                Upload Your Own Videos
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                BrandStage welcomes high-quality videos you've produced independently. Before uploading, please review 
                our standards below. Videos that meet these criteria will be approved for display on your profile. 
                Use our self-assessment checklist to evaluate your content before submission.
              </p>
            </div>

            {/* Self-Assessment Checklist */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Self-Assessment Checklist</h3>
              <p className="text-zinc-400 text-sm mb-6">Review each criterion before uploading. Your video should meet ALL requirements.</p>
              
              <SelfAssessmentChecklist />
            </div>

            {/* Technical Requirements */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Technical Requirements</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left py-3 text-zinc-400 font-medium">Criterion</th>
                      <th className="text-left py-3 text-zinc-400 font-medium">Minimum</th>
                      <th className="text-left py-3 text-zinc-400 font-medium">Recommended</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-300">
                    <tr className="border-b border-zinc-800">
                      <td className="py-3 font-medium text-white">Resolution</td>
                      <td className="py-3">720p (1280×720)</td>
                      <td className="py-3 text-green-400">1080p (1920×1080)</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-3 font-medium text-white">Aspect Ratio</td>
                      <td className="py-3">16:9 or 9:16</td>
                      <td className="py-3 text-green-400">16:9 (landscape)</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-3 font-medium text-white">Frame Rate</td>
                      <td className="py-3">24fps</td>
                      <td className="py-3 text-green-400">30fps</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-3 font-medium text-white">Duration</td>
                      <td className="py-3">30 seconds</td>
                      <td className="py-3 text-green-400">1-3 minutes</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-3 font-medium text-white">File Format</td>
                      <td className="py-3">MP4, MOV, WebM</td>
                      <td className="py-3 text-green-400">MP4 (H.264)</td>
                    </tr>
                    <tr className="border-b border-zinc-800">
                      <td className="py-3 font-medium text-white">File Size</td>
                      <td className="py-3">Max 500MB</td>
                      <td className="py-3 text-green-400">Under 200MB</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-medium text-white">Audio</td>
                      <td className="py-3">Audible speech</td>
                      <td className="py-3 text-green-400">Clear, no background noise</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Visual Standards */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Visual Standards</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Do's */}
                <div>
                  <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Do's
                  </h4>
                  <ul className="space-y-2">
                    {[
                      "Well-lit face, no harsh shadows",
                      "Clean, professional background",
                      "Camera at eye level",
                      "Stable footage (tripod recommended)",
                      "Professional or smart-casual attire",
                      "Look at the camera when speaking",
                      "Frame from chest up for interviews"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Don'ts */}
                <div>
                  <h4 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Don'ts
                  </h4>
                  <ul className="space-y-2">
                    {[
                      "Backlit subjects (window behind)",
                      "Cluttered or distracting backgrounds",
                      "Shaky handheld footage",
                      "Poor audio (echo, wind, traffic)",
                      "Casual attire (unless brand-appropriate)",
                      "Reading obviously from a script",
                      "Visible competitor branding"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Audio Standards */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-[#C41E3A]" />
                Audio Standards
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h4 className="text-white font-semibold text-sm mb-2">Clarity</h4>
                  <p className="text-zinc-400 text-sm">Speech must be clear and easily understandable. No mumbling or unclear pronunciation.</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h4 className="text-white font-semibold text-sm mb-2">Background Noise</h4>
                  <p className="text-zinc-400 text-sm">Minimal to no background noise. Avoid traffic, AC hum, echoes, or other distractions.</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h4 className="text-white font-semibold text-sm mb-2">Volume Levels</h4>
                  <p className="text-zinc-400 text-sm">Consistent audio levels throughout. No sudden loud or quiet sections.</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <p className="text-amber-300 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Tip:</strong> Use an external microphone (lapel mic or USB mic) rather than your device's built-in microphone for significantly better audio quality.</span>
                </p>
              </div>
            </div>

            {/* Content Standards */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Content Standards</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">Professional & Business-Appropriate</h4>
                    <p className="text-zinc-400 text-sm">Content should be suitable for a professional B2B context. No offensive, discriminatory, or inappropriate material.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">Authentic & Truthful</h4>
                    <p className="text-zinc-400 text-sm">Claims made in videos should be accurate and verifiable. No misleading statements or false representations.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">Rights & Permissions</h4>
                    <p className="text-zinc-400 text-sm">You must own or have rights to all content (footage, music, images). Any individuals appearing must have given consent.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">No Competitor Branding</h4>
                    <p className="text-zinc-400 text-sm">Videos should not prominently feature competitor logos, products, or branding.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Approval Process */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Approval Process</h3>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center mx-auto mb-2 font-bold">1</div>
                  <h4 className="text-white font-semibold text-sm mb-1">Self-Assess</h4>
                  <p className="text-zinc-400 text-xs">Use checklist above to verify quality</p>
                </div>
                <div className="hidden md:flex items-center text-zinc-600">→</div>
                <div className="flex-1 bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center mx-auto mb-2 font-bold">2</div>
                  <h4 className="text-white font-semibold text-sm mb-1">Upload</h4>
                  <p className="text-zinc-400 text-xs">Submit video for review</p>
                </div>
                <div className="hidden md:flex items-center text-zinc-600">→</div>
                <div className="flex-1 bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center mx-auto mb-2 font-bold">3</div>
                  <h4 className="text-white font-semibold text-sm mb-1">Review</h4>
                  <p className="text-zinc-400 text-xs">BrandStage reviews (24-48 hrs)</p>
                </div>
                <div className="hidden md:flex items-center text-zinc-600">→</div>
                <div className="flex-1 bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center mx-auto mb-2 font-bold">4</div>
                  <h4 className="text-white font-semibold text-sm mb-1">Published</h4>
                  <p className="text-zinc-400 text-xs">Approved videos go live</p>
                </div>
              </div>
              <p className="text-zinc-500 text-sm mt-4 text-center">
                Videos not meeting standards will receive feedback on required improvements.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-[#C41E3A]/20 to-[#1E3A5F]/20 border border-[#C41E3A]/30 rounded-2xl p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">Ready to Upload?</h3>
              <p className="text-zinc-400 mb-4">Ensure your video meets all standards, then add it to your profile.</p>
              <Link 
                to="/profile/create"
                className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-xl font-bold transition-colors"
              >
                <FileVideo className="w-4 h-4" />
                Go to Profile
              </Link>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-12 pt-8 border-t border-zinc-800">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#C41E3A]" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              {
                q: "Can I use videos shot on my smartphone?",
                a: "Yes, modern smartphones can capture excellent quality video. Ensure you shoot in 1080p or 4K, use good lighting, and stabilize your phone (tripod or stable surface). Audio is often the weak point—consider using an external microphone."
              },
              {
                q: "What if my video is rejected?",
                a: "You'll receive specific feedback on what needs improvement. Common issues include poor audio, inadequate lighting, or shaky footage. You can re-submit after making corrections."
              },
              {
                q: "Can I upload videos produced by another agency?",
                a: "Yes, as long as you own the rights to the content and it meets our quality standards. Ensure you have documentation of ownership or licensing."
              },
              {
                q: "How long does approval take?",
                a: "Typically 24-48 hours during business days. You'll receive an email notification once your video has been reviewed."
              },
              {
                q: "Is there a cost for BrandStage to produce videos?",
                a: "BrandStage video production is a premium service. Contact us for a custom quote based on your requirements, location, and video type."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors"
                >
                  <span className="text-white font-medium text-sm">{faq.q}</span>
                  {expandedFaq === i ? (
                    <ChevronUp className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === i && (
                  <div className="px-4 pb-4 text-zinc-400 text-sm">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="mt-8 text-center text-zinc-500 text-sm">
          <p>Questions about video standards? <a href="mailto:operations@brandstage-media.com" className="text-[#C41E3A] hover:underline">Contact us</a></p>
        </div>
      </div>
    </div>
  );
}

// Interactive Self-Assessment Checklist Component
function SelfAssessmentChecklist() {
  const [checks, setChecks] = useState({});
  
  const categories = [
    {
      name: "Technical Quality",
      icon: <Monitor className="w-4 h-4" />,
      items: [
        { id: "resolution", label: "Video is at least 720p resolution (1080p preferred)" },
        { id: "stable", label: "Footage is stable (no excessive shaking)" },
        { id: "focus", label: "Subject is in focus throughout" },
        { id: "format", label: "File is MP4, MOV, or WebM format" }
      ]
    },
    {
      name: "Lighting",
      icon: <Sun className="w-4 h-4" />,
      items: [
        { id: "welllit", label: "Subject's face is well-lit and clearly visible" },
        { id: "noshadows", label: "No harsh shadows on face" },
        { id: "nobacklit", label: "Not backlit (no bright window/light behind subject)" },
        { id: "consistent", label: "Lighting is consistent throughout video" }
      ]
    },
    {
      name: "Audio",
      icon: <Mic className="w-4 h-4" />,
      items: [
        { id: "audible", label: "Speech is clear and easily understood" },
        { id: "nonoise", label: "No distracting background noise" },
        { id: "noecho", label: "No echo or reverb issues" },
        { id: "volume", label: "Audio volume is consistent" }
      ]
    },
    {
      name: "Presentation",
      icon: <Shirt className="w-4 h-4" />,
      items: [
        { id: "attire", label: "Professional or appropriate attire" },
        { id: "background", label: "Clean, uncluttered background" },
        { id: "framing", label: "Good framing (head and shoulders visible)" },
        { id: "eyeline", label: "Camera at eye level or slightly above" }
      ]
    },
    {
      name: "Content",
      icon: <FileVideo className="w-4 h-4" />,
      items: [
        { id: "professional", label: "Content is professional and business-appropriate" },
        { id: "authentic", label: "Delivery feels natural, not overly scripted" },
        { id: "nocompetitor", label: "No visible competitor branding" },
        { id: "rights", label: "I own or have rights to all content" }
      ]
    }
  ];

  const totalItems = categories.reduce((acc, cat) => acc + cat.items.length, 0);
  const checkedItems = Object.values(checks).filter(Boolean).length;
  const allChecked = checkedItems === totalItems;

  const toggleCheck = (id) => {
    setChecks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div>
      <div className="space-y-6">
        {categories.map((category, i) => (
          <div key={i}>
            <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
              <span className="text-[#C41E3A]">{category.icon}</span>
              {category.name}
            </h4>
            <div className="space-y-2">
              {category.items.map((item) => (
                <label 
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    checks[item.id] ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800/50 border border-transparent hover:bg-zinc-800'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checks[item.id] || false}
                    onChange={() => toggleCheck(item.id)}
                    className="w-4 h-4 rounded border-zinc-600 text-green-500 focus:ring-green-500 focus:ring-offset-0 bg-zinc-700"
                  />
                  <span className={`text-sm ${checks[item.id] ? 'text-green-300' : 'text-zinc-300'}`}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Progress & Result */}
      <div className="mt-6 p-4 rounded-xl border bg-zinc-800/50 border-zinc-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-zinc-400 text-sm">Checklist Progress</span>
          <span className="text-white font-semibold">{checkedItems} / {totalItems}</span>
        </div>
        <div className="h-2 bg-zinc-700 rounded-full overflow-hidden mb-3">
          <div 
            className={`h-full transition-all duration-300 ${allChecked ? 'bg-green-500' : 'bg-[#C41E3A]'}`}
            style={{ width: `${(checkedItems / totalItems) * 100}%` }}
          />
        </div>
        {allChecked ? (
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>Your video appears to meet all standards. Ready to upload!</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span>Please ensure all items are checked before uploading.</span>
          </div>
        )}
      </div>
    </div>
  );
}
